import React, { memo, useState } from 'react';
import { Stack } from 'investira.react.components';
import { PageTemplate } from '../../components/templates';
import { useComparadorFundosContext } from '../../contexts/ComparadorFundosContext';
import ComparadorFundosComparisonShell from './_components/ComparadorFundosComparisonShell';
import ComparadorFundosSelectionShell from './_components/ComparadorFundosSelectionShell';
import { buildComparadorFundosPrimaryLabel } from './_shared/comparadorFundos.labels';
import { COMPARADOR_FUNDOS_MAX_ITEMS } from './_shared/comparadorFundos.helpers';
import utils from '../../utils';

/** Resolve o nome textual da gestora nos formatos praticados pelos serviços. */
function pvResolveGestorLabel(pGestor) {
  if (pGestor && typeof pGestor === 'object') {
    return pGestor.pessoa_fantasia || pGestor.nome || null;
  }

  return pGestor || null;
}

/**
 * Combina somente o payload escolhido na busca e o detalhe real progressivo.
 * Campos ainda não resolvidos permanecem nulos para os placeholders da tela.
 *
 * @param {string} pFundoId Identificador canônico do fundo.
 * @param {Object} pSelectedFundosMap Payloads conhecidos da seleção.
 * @param {Object} pFundosState Estado real por fundo e fonte.
 * @returns {Object} Fundo pronto para os shells atuais.
 */
function pvBuildComparisonFundo(pFundoId, pSelectedFundosMap, pFundosState) {
  const xSelectedFundo = pSelectedFundosMap[pFundoId] || {};
  const xDetalhe = pFundosState[pFundoId]?.sources?.detalhe?.data || {};

  return {
    ...xSelectedFundo,
    ...xDetalhe,
    ifeed_ativo_id: pFundoId,
    ativo: xDetalhe.ativo || xSelectedFundo.ativo || null,
    ticker: xDetalhe.ticker || xSelectedFundo.ticker || null,
    gestor: pvResolveGestorLabel(xDetalhe.gestor || xSelectedFundo.gestor),
    gestorDetalhe: xDetalhe.gestor && typeof xDetalhe.gestor === 'object' ? xDetalhe.gestor : null,
    benchmarkIndicadorId:
      xDetalhe.fundo?.benchmark_indicador_id || xSelectedFundo.benchmarkIndicadorId || null,
    ativoTipoId: xSelectedFundo.ativoTipoId || null
  };
}

const ComparadorFundosContent = memo(() => {
  const { state, actions } = useComparadorFundosContext();
  const [highlightEnabled, setHighlightEnabled] = useState(true);
  const xComparisonFundos = state.selectedFundosIds
    .filter(pFundoId => {
      const xDetalhe = state.fundos[pFundoId]?.sources?.detalhe?.data;

      return xDetalhe?.mercado === 'FND';
    })
    .map(pFundoId => {
      return pvBuildComparisonFundo(pFundoId, state.selectedFundosMap, state.fundos);
    });
  const xCotacoesPorId = state.selectedFundosIds.reduce((pResultado, pFundoId) => {
    const xCotacaoSource = state.fundos[pFundoId]?.sources?.cotacao;

    return {
      ...pResultado,
      [pFundoId]: {
        data: xCotacaoSource?.data || [],
        loading: !xCotacaoSource || ['idle', 'loading'].includes(xCotacaoSource.status),
        error: xCotacaoSource?.status === 'error'
      }
    };
  }, {});

  const xComparisonReady = xComparisonFundos.length >= 2;

  const xTitle = xComparisonReady
    ? xComparisonFundos.map(utils.helpers.getAtivoDescricao).join(' vs ')
    : 'Comparador de Fundos';

  const xDescription = xComparisonReady
    ? `${xComparisonFundos.length} de ${COMPARADOR_FUNDOS_MAX_ITEMS} prontos para comparação.`
    : 'Compare os melhores fundos de investimento do Brasil de forma rápida e fácil. Veja performance, indicadores e encontre o fundo ideal para o seu perfil. Ferramenta de comparação completa para FIIs, multimercados, renda fixa, ações e mais.';

  return (
    <PageTemplate title={xTitle} description={xDescription} maxWidth="xl">
      <Stack spacing={3}>
        <ComparadorFundosSelectionShell
          comparisonFundos={xComparisonFundos}
          ignoredSelectionNotice={state.ignoredSelectionNotice}
          limitNotice={state.limitNotice}
          selectionNotice={state.selectionNotice}
          highlightEnabled={highlightEnabled}
          onHighlightEnabledChange={setHighlightEnabled}
          onAddFundo={actions.handleAddFundo}
          onRemoveFundo={actions.handleRemoveFundo}
          onReplaceFundo={actions.handleReplaceFundo}
        />

        <ComparadorFundosComparisonShell
          comparisonFundos={xComparisonFundos}
          cotacoesPorId={xCotacoesPorId}
          highlightEnabled={highlightEnabled}
        />
      </Stack>
    </PageTemplate>
  );
});

export default ComparadorFundosContent;
